import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import Slide from "@material-ui/core/Slide";
import Modal from '@material-ui/core/Modal';

import modalStyles from 'shared/components/modalStyles';

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
    minWidth: '300px'
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeIcon: {
    position: 'absolute',
    top: 0,
    right: 0,
    color: '#d4d4d4',
    cursor: 'pointer'
  },
  content: {
    backgroundColor: 'white',
    borderRadius: 5,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    overflow: 'hidden'
  },
  ...modalStyles()
}));

const Modal2 = props => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideDirection, setSlideDirection] = useState("left");
  const [error, setError] = useState();
  const [clicked, setClicked] = useState(false);
  const classes = useStyles();

  if (!props.slides) {
    return null;
  }

  props.slides.forEach((slide, i) => slide.index = i);

  // process a button's callback function.
  // if it's a synchronous function and it returns true
  // then we'll advance to the next slide.
  // if it's async then we'll advance if it resolves
  // handling clicked status to prevent double clicks
  const callback = async fn => {
    setClicked(true);
    if (fn && !clicked) {
      let resp;
      try {
        resp = fn();
      } catch (error) {
        setClicked(false);
        throw error
      }

      if (resp instanceof Promise) {
        try {
          await resp;
        } finally {
          setClicked(false);
        }
      } else {
        setClicked(false);
      }
    }
  }

  const setErrorMessage = err => {
    let msg;
    if (typeof err === 'string') {
      msg = err;
    } else {
      msg = err.message || err.error || err.jsx || JSON.stringify(err);
    }

    setError(msg);
  }

  const nextSlide = async onNext => {
    const slide = props.slides[currentSlide];

    try {
      if (onNext) await callback(onNext);
      if (slide.onNext) await callback(slide.onNext);

      setError(null);
      setSlideDirection("left");
      setCurrentSlide(currentSlide + 1);

    } catch (err) {
      setErrorMessage(err);
    }
  };

  const reset = () => {
    setError(null);
    setCurrentSlide(0);
    setSlideDirection('left');
  }

  const onClose = () => {
    if (props.onClose) {
      props.onClose();
    }

    // reset modal once closed
    setTimeout(() => reset(), 500);
  };

  const prevSlide = async onBack => {
    const slide = props.slides[currentSlide];

    setSlideDirection("right");

    try {
      if (slide.onBack) await callback(slide.onBack);
    } catch (err) {
      console.error(err);
    }

    if (slide.onBackClose) {
      onClose();
      setCurrentSlide(0);
    } else {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const onFinish = async onNext => {
    const slide = props.slides[currentSlide];

    try {
      if (onNext) await callback(onNext);
      if (slide.onNext) await callback(slide.onNext);

      setError(null);
      if (!slide.skipClose) {
        onClose();
      }

    } catch (err) {
      setErrorMessage(err);
    }
  };

  const isLastSlide = currentSlide === props.slides.length - 1;

  const renderError = () => {
    if (error) {
      return (
        <p className={classes.modalText} style={{ textAlign: 'left', margin: '0.8rem 0', fontSize: '1rem', color: 'red' }}>
          {error}
        </p>
      );
    }
    return null;
  }

  const renderButtons = () => {
    const slide = props.slides[currentSlide];

    if (slide.buttons) {
      let buttons = slide.buttons.map(button => {
        let label, onClick;
        if (isLastSlide) {
          label = button.nextLabel || 'Finish';
          onClick = () => onFinish(button.onNext);
        } else if (button.finishLabel) {
          label = button.finishLabel;
          onClick = () => onFinish(button.onFinish);
        } else {
          label = button.nextLabel || 'Next';
          onClick = () => nextSlide(button.onNext);
        }

        return (
          <button className={classes.modalButtonVertical} onClick={onClick} ref={button.ref}>
            {label}
          </button>
        );
      });

      return (
        <div className={classes.modalButtonsVertical}>
          {buttons}
          {currentSlide > 0 && !slide.noBack && (
            <button className={classes.modalButtonVerticalWhite} onClick={prevSlide}>
              {slide.prevLabel || "Back"}
            </button>
          )}
        </div>
      );

    } else {
      return (
        <div className={classes.modalButtons}>
          {(currentSlide > 0 || slide.forceBack) && !slide.noBack && (
            <button className={classes.modalButtonWhite} onClick={prevSlide}>
              {slide.prevLabel || "Back"}
            </button>
          )}
          {isLastSlide ? (
            <button className={classes.modalButton} onClick={() => onFinish()} ref={props.slides[currentSlide].nextRef}>
              {slide.nextLabel || "Finish"}
            </button>
          ) : (
            <button className={classes.modalButton} onClick={() => nextSlide()} ref={props.slides[currentSlide].nextRef}>
              {slide.nextLabel || "Next"}
            </button>
          )}
        </div>
      );
    }
  }

  const renderSlide = () => {
    const slide = props.slides[currentSlide];
    return (
      <Slide direction={slideDirection} key={currentSlide} in={slide.index === currentSlide}>
        <div>
          {slide.component}
          {renderError()}
          {renderButtons()}
        </div>
      </Slide>
    );
  };

  return (
    <Modal open={props.open} onClose={onClose} className={classes.container}>
      <div className={classes.wrapper}>
        <CloseIcon className={classes.closeIcon} onClick={onClose} />
        <div className={classes.content}>
          {renderSlide()}
        </div>
      </div>
    </Modal>
  );
};

export default Modal2;
